import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlQueryResult} from 'features/core/shared/ql/builder/ql-query-builder';
import {QlMessageSeverity} from 'features/core/shared/ql/data/enum/ql-message-severity.enum';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {MvsFormValueListEntryDto} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {
    ProvisionSimulationDimensionOptionsRequestDto
} from '../../model/dto/api/provision-simulation-dimension-options-request.dto';
import {
    ProvisionSimulationDimensionOptionsResultDto
} from '../../model/dto/api/provision-simulation-dimension-options-result.dto';

/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: DataProvider
 * Reason: Builds the `select` value lists (and the degrade-to-`autoCompleteFkey` decision) for the
 *     FKEY dimensions of the provision simulation form, entirely via QL in cp — no `FormService`
 *     involvement (explicit user decision, capability-matrix FD-3: "Bitte das für cp direkt via QL
 *     umsetzen nicht den FormService anpassen!"). One batched QL request per {@link loadOptions}
 *     call — never one call per field. Below the row-count threshold a dimension gets a complete
 *     `select` dropdown with a search filter; at or above it, `autoCompleteFkey` (G-10). Colliding
 *     labels are disambiguated (FD-8) — measured for `cp.ProvisionAgent` ("Oliver Batz" ×2, via
 *     `am.Agent.shortName`) and `cm.ContractType` ("Glasbruchschäden" ×2, IDs 47/54, via its own
 *     `shortName`). Request/result shapes live in `model/dto/api/` (STRUCT-002: one interface per
 *     file), not inline here.
 */

/**
 * Request/result key of the fixed "Berater" field (`mainProvisionAgentDtoId` → `cp.ProvisionAgent`).
 * Not a `simulationForm` dimension, but loaded through the same batched request and the same
 * threshold/disambiguation machinery.
 */
export const CP_SIMULATION_AGENT_OPTIONS_KEY = 'mainProvisionAgent';

/**
 * Row-count threshold above which a dimension degrades from `select` to `autoCompleteFkey`
 * (capability-matrix G-10). Mirrors the platform constant `FormService.VALUE_LIST_TRANSFER_LIMIT`
 * (`core/form/FormService.java:96`) — duplicated here only because that Java constant has no
 * TypeScript-reachable counterpart across the language boundary (G-15: no
 * `VALUE_LIST_TRANSFER_LIMIT` / `transferLimit` symbol exists anywhere under `features/`). The
 * duplication is safe ONLY because {@link loadOptions} always requests `THRESHOLD + 1` rows and
 * degrades on overflow (G-10): a drift between the two numbers can change WHICH control a field
 * gets, never whether the result is correct — a `select` is either the complete list or it is never
 * built at all (G-6).
 */
export const CP_SIMULATION_VALUE_LIST_THRESHOLD = 1000;

@Injectable({providedIn: 'root'})
export class CpProvisionSimulationDimensionOptionsService {

    constructor(private qlService: QlService) {
    }

    /**
     * Loads the value lists / degrade decision of every requested dimension PLUS the fixed
     * `cp.ProvisionAgent` "Berater" field, in ONE batched QL request.
     *
     * A dimension always resolves to EXACTLY one of `select` (complete list) or `autoCompleteFkey`
     * — never a silent third state, and never a `select` with an incomplete list (G-6). A transport
     * failure or a per-pipe QL error (unknown alias/`selectAttribute` — QL answers those with an
     * empty `data` array plus an error message, not an HTTP failure) degrades EVERY affected
     * dimension to `autoCompleteFkey` rather than being read as "this dimension has zero options":
     * that reading would leave a field rendering an empty `select`, which looks like a plausible (if
     * unlikely) answer instead of the failure it actually is (capability-matrix V-16).
     */
    loadOptions(
        dimensions: ProvisionSimulationDimensionOptionsRequestDto[]
    ): Observable<Map<string, ProvisionSimulationDimensionOptionsResultDto>> {

        const allKeys: string[] = [...dimensions.map(dimension => dimension.key), CP_SIMULATION_AGENT_OPTIONS_KEY];

        let builder = this.qlService.builder().showErrors(false);

        for (const dimension of dimensions) {
            builder = builder.query(this.buildGenericQuery(dimension));
        }

        builder = builder.query(this.buildAgentQuery());

        return builder.execute().pipe(
            map(result => {
                const resolved = new Map<string, ProvisionSimulationDimensionOptionsResultDto>();
                for (const dimension of dimensions) {
                    resolved.set(dimension.key, this.resolveGeneric(result, dimension));
                }
                resolved.set(CP_SIMULATION_AGENT_OPTIONS_KEY, this.resolveAgent(result));
                return resolved;
            }),
            catchError(error => {
                // The WHOLE batched request failed transport-side (network/5xx). Degrade every
                // requested field rather than let an absent response collapse to "zero options" —
                // that is exactly the confusion V-16 forbids.
                console.error(
                    'cp-provision-simulation: Optionsabfrage der FKEY-Dropdowns fehlgeschlagen '
                    + '(Transportfehler) — alle Felder degradieren auf autoCompleteFkey.', error);
                const degraded = new Map<string, ProvisionSimulationDimensionOptionsResultDto>();
                for (const key of allKeys) {
                    degraded.set(key, {key, uiInputControl: 'autoCompleteFkey'});
                }
                return of(degraded);
            })
        );
    }

    private queryNameOf(key: string): string {
        return 'cpSimOptions_' + key;
    }

    /**
     * Generic id/name entity query, used for the nine `cm.*` contract dimensions. Adds the FD-8
     * discriminator column ONLY when the request names one (`discriminatorAttribute`) — most of the
     * nine entities have no such column, so this must stay opt-in per request, never a blanket
     * `shortName` added to every query (see the field's Javadoc on
     * {@link ProvisionSimulationDimensionOptionsRequestDto}).
     */
    private buildGenericQuery(
        dimension: ProvisionSimulationDimensionOptionsRequestDto
    ): QlQueryDto {
        return {
            name: this.queryNameOf(dimension.key),
            type: 'view',
            start: {
                type: 'entity',
                name: dimension.dataType,
                as: 'e',
                fields: dimension.discriminatorAttribute
                    ? [{name: 'id'}, {name: 'name'}, {name: dimension.discriminatorAttribute}]
                    : [{name: 'id'}, {name: 'name'}]
            },
            sortings: [new Sorting('e.name', true)],
            paging: PagingDto.createSize(CP_SIMULATION_VALUE_LIST_THRESHOLD + 1)
        };
    }

    /**
     * `cp.ProvisionAgent` has no `name` column — its label is `am.Agent.name`, reached through an
     * EXPLICIT join (capability-matrix FD-3/G-11), never through `ProvisionAgent.getName()`, whose
     * `LazyInitializationException` branch falls back to the numeric id as a string. `am.Agent
     * .shortName` rides along in the same join as the FD-8 disambiguation discriminator — one join,
     * both problems solved, same precedent as `cp-provision-agents.component.ts:167-180`. Filtered
     * to `entityStatus = active` (351 of 355 measured) — the 4 `deleted` rows do not belong in the
     * dropdown.
     */
    private buildAgentQuery(): QlQueryDto {
        return {
            name: this.queryNameOf(CP_SIMULATION_AGENT_OPTIONS_KEY),
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgent',
                as: 'e',
                fields: [{name: 'id'}],
                joins: [
                    {
                        type: 'entity', name: 'agent', as: 'ag', joinType: 'left',
                        fields: [{name: 'name'}, {name: 'shortName'}]
                    }
                ]
            },
            filters: [
                FilterCriteria.create('e.entityStatus', FilterCriteria.cOperatorEqual, EntityStatusEnum.active)
            ],
            sortings: [new Sorting('ag.name', true), new Sorting('e.id', true)],
            paging: PagingDto.createSize(CP_SIMULATION_VALUE_LIST_THRESHOLD + 1)
        };
    }

    /** Per-pipe QL error (V-16) and a row count above the threshold (G-10) both degrade the field. */
    private resolveGeneric(
        result: QlQueryResult,
        dimension: ProvisionSimulationDimensionOptionsRequestDto
    ): ProvisionSimulationDimensionOptionsResultDto {

        const key = dimension.key;
        const pipeResult = result.getPipeResult(this.queryNameOf(key));

        if (this.hasError(pipeResult)) {
            console.error(
                'cp-provision-simulation: Optionsabfrage für Dimension "' + key + '" meldete einen '
                + 'QL-Fehler — Feld degradiert auf autoCompleteFkey.', pipeResult?.messages);
            return {key, uiInputControl: 'autoCompleteFkey'};
        }

        const rows: any[] = pipeResult?.data ?? [];

        if (rows.length > CP_SIMULATION_VALUE_LIST_THRESHOLD) {
            return {key, uiInputControl: 'autoCompleteFkey'};
        }

        const discriminatorColumn = dimension.discriminatorAttribute
            ? 'e.' + dimension.discriminatorAttribute
            : null;

        const entries = rows.map(row => ({
            key: row['e.id'],
            label: row['e.name'] ?? '',
            discriminator: discriminatorColumn ? row[discriminatorColumn] as string : undefined
        }));

        return {key, uiInputControl: 'select', valueList: this.disambiguate(entries)};
    }

    private resolveAgent(result: QlQueryResult): ProvisionSimulationDimensionOptionsResultDto {

        const key = CP_SIMULATION_AGENT_OPTIONS_KEY;
        const pipeResult = result.getPipeResult(this.queryNameOf(key));

        if (this.hasError(pipeResult)) {
            console.error(
                'cp-provision-simulation: Optionsabfrage für "Berater" meldete einen QL-Fehler — '
                + 'Feld degradiert auf autoCompleteFkey.', pipeResult?.messages);
            return {key, uiInputControl: 'autoCompleteFkey'};
        }

        const rows: any[] = pipeResult?.data ?? [];

        if (rows.length > CP_SIMULATION_VALUE_LIST_THRESHOLD) {
            return {key, uiInputControl: 'autoCompleteFkey'};
        }

        // `ag.name` is preferred; `ag.shortName` and the id are last-resort fallbacks for a row
        // whose am.Agent join legitimately carries neither — measured: none of the 351 active rows
        // do, so this is a safety net, not the expected path (unlike ProvisionAgent.getName(), which
        // this query never calls, see the class Javadoc).
        const entries = rows.map(row => ({
            key: row['e.id'],
            label: row['ag.name'] || row['ag.shortName'] || ('Provisionskonto ' + row['e.id']),
            discriminator: row['ag.shortName'] as string
        }));

        return {key, uiInputControl: 'select', valueList: this.disambiguate(entries)};
    }

    private hasError(pipeResult: any): boolean {
        return !!pipeResult?.messages?.some((message: any) => message.severity === QlMessageSeverity.error);
    }

    /**
     * Disambiguates option labels that collide (FD-8) — comparison runs on the NORMALIZED label
     * (case folded, whitespace collapsed), so "Oliver Batz" and "Oliver  Batz" count as the same
     * text and the collision is not missed. A colliding group first tries the caller-supplied
     * `discriminator` (`am.Agent.shortName` for the Berater field, `cm.ContractType.shortName` for
     * that dimension; absent for every other dimension); if that still collides — or none was
     * supplied — the id is the final, always-unique tiebreaker (the rule TERMINATES). Only entries
     * that actually collide get a suffix; every other entry renders unchanged, so a field without
     * any collision is untouched by this method.
     *
     * `null`, `undefined`, `''` and a whitespace-only string ALL count as "no discriminator" — the
     * `discriminatorKey` below is always run through {@link normalize}, the SAME function that folds
     * a blank/whitespace-only value down to `''`, and every branch below tests that already-folded
     * key rather than the raw field. `cm.ContractType.shortName` is `@Column(length = 14)` and
     * NULLABLE (`am.Agent.shortName` is `@NotNull`, but this code does not rely on that difference —
     * it treats both uniformly). Proven in the spec for the real measured collision — "Glasbruchschäden"
     * ids 47/54, one row with a `null` `shortName`.
     *
     * The resulting label is DISPLAY ONLY and, by construction, UNSTABLE: adding a third, later
     * "Oliver Batz" can change what the first two currently show. That is tolerable here because
     * `MvsFormFieldSelectDropdownComponent` writes `optionValue="key"` — the id — never the label
     * (`mvs-form-field-select-dropdown.component.ts:27`), so nothing downstream may treat this label
     * as an identity (the inverse lesson of `cc.FieldValueList.name`, which IS identity).
     */
    private disambiguate(
        entries: { key: any, label: string, discriminator?: string }[]
    ): MvsFormValueListEntryDto[] {

        const normalize = (value: string): string => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

        const byLabel = new Map<string, typeof entries>();
        for (const entry of entries) {
            const normalized = normalize(entry.label);
            const group = byLabel.get(normalized);
            if (group) {
                group.push(entry);
            } else {
                byLabel.set(normalized, [entry]);
            }
        }

        const result: MvsFormValueListEntryDto[] = [];

        for (const group of byLabel.values()) {

            if (group.length === 1) {
                result.push(MvsFormValueListEntryDto.createBasic(group[0].key, group[0].label));
                continue;
            }

            const byDiscriminator = new Map<string, typeof entries>();
            for (const entry of group) {
                // `normalize()` itself coalesces null/undefined/blank to `''` — no separate ternary
                // needed, and no risk of the raw (unfolded) value leaking into the grouping key.
                const discriminatorKey = normalize(entry.discriminator);
                const subGroup = byDiscriminator.get(discriminatorKey);
                if (subGroup) {
                    subGroup.push(entry);
                } else {
                    byDiscriminator.set(discriminatorKey, [entry]);
                }
            }

            for (const [discriminatorKey, subGroup] of byDiscriminator) {
                if (discriminatorKey && subGroup.length === 1) {
                    const entry = subGroup[0];
                    result.push(MvsFormValueListEntryDto.createBasic(
                        entry.key, entry.label + ' (' + entry.discriminator + ')'));
                } else {
                    // Still colliding after the discriminator (or none was supplied): the id is the
                    // rule's termination — always unique, never `null`, never duplicated.
                    for (const entry of subGroup) {
                        result.push(MvsFormValueListEntryDto.createBasic(
                            entry.key, entry.label + ' (Id ' + entry.key + ')'));
                    }
                }
            }
        }

        return result;
    }

}
